/**
 * Scoped Reset Dialog Component
 * Phase B Clean Slate Hardening
 * 
 * Features:
 * - Preview of what will be deleted
 * - Typed confirmation token requirement
 * - Safe default (AI Engine only, not production data)
 */
import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { 
  AlertTriangle, 
  Trash2, 
  Shield, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Database,
  FileText,
  Layers,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { adminFetchHeaders } from '@/hooks/useAdminFetch';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
const API = ''; // Relative URL - browser uses same origin

const getAuthHeaders = () => adminFetchHeaders(true);

// Danger level colors
const DANGER_COLORS = {
  low: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  medium: 'bg-orange-100 text-orange-800 border-orange-300',
  high: 'bg-red-100 text-red-800 border-red-300'
};

const ScopedResetDialog = ({ 
  isOpen, 
  onClose, 
  onResetComplete,
  examProfileId = null 
}) => {
  const [actions, setActions] = useState([]);
  const [selectedAction, setSelectedAction] = useState('ai_engine_artifacts');
  const [preview, setPreview] = useState(null);
  const [confirmationInput, setConfirmationInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Load available actions
  useEffect(() => {
    if (isOpen) {
      fetchActions();
    }
  }, [isOpen]);

  // Load preview when action changes
  useEffect(() => {
    if (isOpen && selectedAction) {
      fetchPreview();
    }
  }, [isOpen, selectedAction, examProfileId]);

  const fetchActions = async () => {
    try {
      const response = await fetch(`${API}/api/admin/reset/actions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions || []);
      }
    } catch (error) {
      console.error('Failed to fetch reset actions:', error);
    }
  };

  const fetchPreview = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/admin/reset/preview/${selectedAction}`;
      if (examProfileId) {
        url += `?exam_profile_id=${examProfileId}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      }
    } catch (error) {
      console.error('Failed to fetch preview:', error);
      toast.error('Fehler beim Laden der Vorschau');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!preview) return;
    
    // Verify confirmation token
    if (confirmationInput !== preview.confirmation_token_required) {
      toast.error(`Bitte geben Sie "${preview.confirmation_token_required}" ein um fortzufahren`);
      return;
    }

    setExecuting(true);
    try {
      const response = await fetch(`${API}/api/admin/reset/execute`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action_type: selectedAction,
          confirmation_token: confirmationInput,
          exam_profile_id: examProfileId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Reset erfolgreich: ${data.total_deleted} Einträge gelöscht`);
        onResetComplete && onResetComplete(data);
        handleClose();
      } else {
        toast.error(data.error || 'Reset fehlgeschlagen');
      }
    } catch (error) {
      console.error('Reset failed:', error);
      toast.error('Reset fehlgeschlagen: ' + error.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleClose = () => {
    setConfirmationInput('');
    setPreview(null);
    onClose();
  };

  const selectedActionConfig = actions.find(a => a.action_id === selectedAction);
  const isTokenCorrect = preview && confirmationInput === preview.confirmation_token_required;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Trash2 className="w-6 h-6 text-orange-600" />
            Scoped Reset
          </DialogTitle>
          <DialogDescription>
            Sichere, kontrollierte Löschung von AI Engine Daten. 
            Produktionsdaten (Fragen, Mock Exams, Benutzer) werden NIEMALS gelöscht.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Action Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Reset-Aktion wählen</Label>
            <div className="grid grid-cols-1 gap-2">
              {actions.map(action => (
                <Card 
                  key={action.action_id}
                  className={`cursor-pointer transition-all ${
                    selectedAction === action.action_id 
                      ? 'ring-2 ring-blue-500 bg-blue-50' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedAction(action.action_id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {action.action_id === 'ai_engine_artifacts' ? (
                          <Database className="w-5 h-5 text-orange-500" />
                        ) : (
                          <FileText className="w-5 h-5 text-yellow-500" />
                        )}
                        <span className="font-medium">{action.name}</span>
                      </div>
                      <Badge className={DANGER_COLORS[action.danger_level] || DANGER_COLORS.medium}>
                        {action.danger_level === 'low' ? 'Niedrig' : 
                         action.danger_level === 'medium' ? 'Mittel' : 'Hoch'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 ml-7">
                      {action.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Preview Section */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2">Lade Vorschau...</span>
            </div>
          ) : preview && (
            <div className="space-y-4">
              {/* Warning Banner */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-orange-800">Vorschau: Zu löschende Daten</h4>
                    <p className="text-sm text-orange-700 mt-1">
                      {preview.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Collections Preview */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Collections ({preview.total_count} Einträge gesamt)
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(preview.collections || {}).map(([name, count]) => (
                    <div 
                      key={name}
                      className={`flex items-center justify-between p-2 rounded ${
                        typeof count === 'number' && count > 0 
                          ? 'bg-red-50 border border-red-200' 
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <span className="text-sm font-mono">{name}</span>
                      <Badge variant={typeof count === 'number' && count > 0 ? 'destructive' : 'secondary'}>
                        {typeof count === 'number' ? count : count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Protected Collections */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-green-800">Geschützte Daten (werden NICHT gelöscht)</h4>
                    <p className="text-sm text-green-700 mt-1">
                      {preview.warning}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(preview.protected_collections || []).slice(0, 8).map(col => (
                        <Badge key={col} variant="outline" className="bg-white text-green-700 border-green-300">
                          <Lock className="w-3 h-3 mr-1" />
                          {col}
                        </Badge>
                      ))}
                      {(preview.protected_collections || []).length > 8 && (
                        <Badge variant="outline" className="bg-white">
                          +{preview.protected_collections.length - 8} weitere
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Input */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-sm font-medium">
                  Bestätigung erforderlich
                </Label>
                <p className="text-sm text-gray-600">
                  Geben Sie <code className="bg-gray-100 px-2 py-1 rounded font-mono text-red-600">
                    {preview.confirmation_token_required}
                  </code> ein um fortzufahren:
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    value={confirmationInput}
                    onChange={(e) => setConfirmationInput(e.target.value)}
                    placeholder={preview.confirmation_token_required}
                    className={`font-mono ${
                      isTokenCorrect ? 'border-green-500 bg-green-50' : ''
                    }`}
                    data-testid="reset-confirmation-input"
                  />
                  {isTokenCorrect && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            onClick={handleExecute}
            disabled={!isTokenCorrect || executing}
            data-testid="execute-reset-btn"
          >
            {executing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Lösche...
              </>
            ) : preview?.total_count === 0 ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Keine Einträge zum Löschen
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                {preview?.total_count || 0} Einträge löschen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScopedResetDialog;
